import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { ArrowDownIcon, ArrowUpIcon } from '@radix-ui/react-icons';
import { ElevatedButton, SegmentedControl } from '@producer/ui-kit';

import { Header } from '../../../app/components/header';
import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { DesktopCreateProjectModal } from './create-project-modal/desktop-create-project.modal';
import { Plus } from '../../../components/icons';
import { createProject } from '../store/projects.slice';
import { ProjectItem } from '../../../components/project-item/ProjectItem';
import { AssetGrid } from '../../../components/grid';
import { Space } from '../../../app/entities/space';
import { Project } from '../../../app/entities/project';
import { uiStore } from '../../../core/stores/ui-store';
import { FloatingActionButton } from '../../../components/buttons/floating-action-button';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { ProjectListItem } from './project-list-item';
import { GridIcon, ListDisplayIcon } from '../../../components/icons';
import { sortBy } from '../../settings/helpers/sortBy';
import { ListView, ListViewHeader } from '../../../components/list-view/list-view';
import { useResponsive } from '../../../hooks/useResponsive';
import { MobileCreateProjectModal } from './create-project-modal/mobile-create-project-modal';
import { useCurrentMember } from '../../../hooks/use-current-member';
import { TaskPropertyButton } from '../../tasks/components/task-property.button';

interface ProjectListProps {
  /** A list of projects **/
  space: Space;
}

export const ProjectList: React.FC<ProjectListProps> = observer(({ space }) => {
  const { isDesktop } = useResponsive();
  const currentMember = useCurrentMember();
  const userPreferences = currentMember?.preferences;

  const [isModalOpen, setIsModalOpen] = useState<null | boolean>(false);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');

  useEffect(() => {
    uiStore.pageTitle = {
      label: 'Projects',
    };

    uiStore.showBack = false;
  }, []);

  const handleSubmit = async (project: any) => {
    await createProject({ spaceId: space._id, ...project });
    setIsModalOpen(false);
  };

  const handleNewProjectClicked = () => {
    if (space.canCreateProject()) {
      setIsModalOpen(true);
    } else {
      setShowLimitReachedModal(true);
    }
  };

  const toggleDisplayLayout = () => {
    currentMember?.toggleProjectsDisplayLayout();
  };

  // Filter projects by archived status
  const { activeProjects, archivedProjects } = useMemo(() => {
    const active = space.projects.filter((p) => !p.archived);
    const archived = space.projects.filter((p) => p.archived);
    return { activeProjects: active, archivedProjects: archived };
  }, [space.projects]);

  // Separate pinned projects from unpinned
  const { pinnedProjects, unpinnedProjects } = useMemo(() => {
    const pinnedIds = userPreferences?.pinnedProjectIds || [];
    const projectsToShow = activeTab === 'active' ? activeProjects : archivedProjects;

    const pinned = projectsToShow.filter((p) => pinnedIds.includes(p._id) && !p.archived);
    const unpinned = projectsToShow.filter((p) => !pinnedIds.includes(p._id) || p.archived);

    return { pinnedProjects: pinned, unpinnedProjects: unpinned };
  }, [activeProjects, archivedProjects, activeTab, userPreferences?.pinnedProjectIds]);

  const order = orderBy[0] === '-' ? 'asc' : 'desc';
  const projects = sortBy(
    activeTab === 'active' ? unpinnedProjects : archivedProjects,
    !orderBy ? 'createdAt' : orderBy.replace('-', ''),
    order
  );
  const sortedPinnedProjects = sortBy(
    pinnedProjects,
    !orderBy ? 'createdAt' : orderBy.replace('-', ''),
    order
  );

  const handleOrder = (value: string) => {
    const currentOrder = orderBy[0] === '-' ? 'desc' : 'asc';
    const current = orderBy.replace('-', '');

    /**
     * 1. If it's not the same as current ordering, set it to desc
     * 2. If it's the same and it's already desc, set it to asc
     * 3. If it's the same and it's already asc, set it to default
     */

    if (value !== current) {
      setOrderBy(`-${value}`);
    } else if (currentOrder === 'desc') {
      setOrderBy(value);
    } else {
      setOrderBy('');
    }
  };

  const tabSelector = (
    <TabSelector>
      <TaskPropertyButton
        label="Active"
        isActive={activeTab === 'active'}
        onClick={() => setActiveTab('active')}
      />
      <TaskPropertyButton
        label="Archived"
        isActive={activeTab === 'archived'}
        onClick={() => setActiveTab('archived')}
      />
    </TabSelector>
  );

  return (
    <Page>
      {showLimitReachedModal && (
        <LimitReachedModal
          type="projects"
          eventName="Add project attempt"
          onCancel={() => setShowLimitReachedModal(false)}
        />
      )}

      <Header title={'Projects'} afterTitle={tabSelector}>
        <SegmentedControl key="change-layout-button" onClick={toggleDisplayLayout}>
          <span data-active={userPreferences?.projectsDisplayLayout !== 'list'}>
            <GridIcon />
          </span>

          <span data-active={userPreferences?.projectsDisplayLayout === 'list'}>
            <ListDisplayIcon />
          </span>
        </SegmentedControl>

        {space!.flags && space!.flags['project.create'] ? (
          <ElevatedButton
            id="create-project"
            text="Create project"
            icon={<Plus />}
            onClick={handleNewProjectClicked}
          />
        ) : null}
      </Header>

      <Container>
        {/* The page header is hidden below the desktop breakpoint, so keep the selector reachable on mobile */}
        <MobileTabSelector>{tabSelector}</MobileTabSelector>

        {space.projects.length === 0 ? (
          <PagePlaceholder
            title="You haven't created a project yet."
            description="Start with one of our templates."
            actionButton={{ text: 'Create project', onClick: () => setIsModalOpen(true) }}
            fullWidth
          />
        ) : (
          <>
            {userPreferences?.projectsDisplayLayout !== 'list' ? (
              <>
                {activeTab === 'active' && sortedPinnedProjects.length > 0 && (
                  <>
                    <SectionTitle>★ Pinned</SectionTitle>
                    <Grid>
                      {sortedPinnedProjects.map((project: Project) => (
                        <ProjectItem key={project._id} project={project} />
                      ))}
                    </Grid>
                    {projects.length > 0 && <SectionTitle>Projects</SectionTitle>}
                  </>
                )}
                <Grid>
                  {projects.map((project: Project) => (
                    <ProjectItem key={project._id} project={project} />
                  ))}
                </Grid>
              </>
            ) : (
              <ListView>
                <ListViewHeader>
                  <HeaderCell />

                  <HeaderCell onClick={() => handleOrder('name')}>
                    Name
                    {orderBy === '-name' && <ArrowDownIcon />}
                    {orderBy === 'name' && <ArrowUpIcon />}
                  </HeaderCell>

                  <HeaderCell onClick={() => handleOrder('progress')}>
                    Progress
                    {orderBy === '-progress' && <ArrowDownIcon />}
                    {orderBy === 'progress' && <ArrowUpIcon />}
                  </HeaderCell>

                  <HeaderCell onClick={() => handleOrder('usedStorage')}>
                    Size
                    {orderBy === '-usedStorage' && <ArrowDownIcon />}
                    {orderBy === 'usedStorage' && <ArrowUpIcon />}
                  </HeaderCell>

                  <HeaderCell onClick={() => handleOrder('lastUpdatedAt')}>
                    Last updated
                    {orderBy === 'lastUpdatedAt' && <ArrowDownIcon />}
                    {orderBy === '-lastUpdatedAt' && <ArrowUpIcon />}
                  </HeaderCell>

                  <HeaderCell onClick={() => handleOrder('dueDate')}>
                    Due date
                    {orderBy === 'dueDate' && <ArrowDownIcon />}
                    {orderBy === '-dueDate' && <ArrowUpIcon />}
                  </HeaderCell>

                  <HeaderCell onClick={() => handleOrder('priority')}>
                    Priority
                    {orderBy === '-priority' && <ArrowDownIcon />}
                    {orderBy === 'priority' && <ArrowUpIcon />}
                  </HeaderCell>

                  {isDesktop && ['owner', 'admin', 'creator'].includes(currentMember?.role || '') && (
                    <HeaderCell>People on project</HeaderCell>
                  )}

                  {/* the width property is to let this empty cell expand and take all the empty space */}
                  <HeaderCell style={{ width: '80%' }} />

                  <th />
                </ListViewHeader>

                <tbody>
                  {activeTab === 'active' && sortedPinnedProjects.length > 0 && (
                    <>
                      <PinnedHeaderRow>
                        <td colSpan={100}>
                          <SectionTitle>★ Pinned</SectionTitle>
                        </td>
                      </PinnedHeaderRow>
                      {sortedPinnedProjects.map((project) => (
                        <ProjectListItem key={project._id} project={project} />
                      ))}
                      {projects.length > 0 && (
                        <PinnedHeaderRow>
                          <td colSpan={100}>
                            <SectionTitle>Projects</SectionTitle>
                          </td>
                        </PinnedHeaderRow>
                      )}
                    </>
                  )}
                  {projects.map((project) => (
                    <ProjectListItem key={project._id} project={project} />
                  ))}
                </tbody>
              </ListView>
            )}
          </>
        )}
      </Container>

      {isModalOpen &&
        (!isDesktop ? (
          <MobileCreateProjectModal
            isOpen={isModalOpen}
            spaceId={space._id}
            onSubmit={handleSubmit}
            onCancel={() => setIsModalOpen(false)}
          />
        ) : (
          <DesktopCreateProjectModal
            spaceId={space._id}
            onCancel={() => setIsModalOpen(false)}
            onSubmit={handleSubmit}
          />
        ))}

      <FloatingActionButton onClick={handleNewProjectClicked} />
    </Page>
  );
});

const Page = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const TabSelector = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
`;

const MobileTabSelector = styled.div`
  padding: 1.2rem 1.6rem 0;

  /* matches the page header's breakpoint, which is hidden below 990px */
  @media screen and (min-width: 990px) {
    display: none;
  }
`;

const Grid = styled(AssetGrid)`
  width: 100%;
  padding: 1.6rem;
`;

export const HeaderCell = styled.th`
  position: relative;
  user-select: none;
  vertical-align: middle;

  &:hover {
    cursor: pointer;
    color: var(--color-grayscale-white);
  }

  svg {
    position: absolute;
    transform: translate(4px, 1px);
    color: var(--color-primary-crayola);
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--color-grayscale-white);
  margin: 1.6rem 1.6rem 0.8rem 1.6rem;
  padding: 0;
`;

const PinnedHeaderRow = styled.tr`
  background: transparent;

  td {
    padding: 0 !important;
    border: none !important;
  }

  &:hover {
    background: transparent !important;
  }
`;
